import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sessionsAPI, languagesAPI, seedAPI, creditsAPI, bookingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Globe, Clock, AlertTriangle, CheckCircle, XCircle, Loader2, Database, Bot, Headphones, Coins, CreditCard, Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

const LN = { en:'English',es:'Spanish',zh:'Mandarin',ar:'Arabic',vi:'Vietnamese',fr:'French',tl:'Tagalog',ko:'Korean',ru:'Russian',pt:'Portuguese',hi:'Hindi' };

export default function ClientDashboard() {
  const { user, acceptAgreement } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [languages, setLanguages] = useState({ sla: [], supported: [] });
  const [showNewSession, setShowNewSession] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [selectedLang, setSelectedLang] = useState('es');
  const [domainLevel, setDomainLevel] = useState('BALANCED');
  const [sessionType, setSessionType] = useState('ai');
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creditSettings, setCreditSettings] = useState({ base_rate_cents: 20, ai_credits_per_minute: 1, human_credits_per_minute: 3 });
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [purchasing, setPurchasing] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingForm, setBookingForm] = useState({ target_language: 'es', domain_level: 'BALANCED', date: null, time: '09:00', duration_minutes: 30, notes: '' });
  const [bookingLoading, setBookingLoading] = useState(false);

  const pollPaymentStatus = useCallback(async (stripeSessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const res = await creditsAPI.getPaymentStatus(stripeSessionId);
      if (res.data.payment_status === 'paid') {
        toast.success(`Payment successful! ${res.data.credits} credits added.`);
        const balRes = await creditsAPI.getBalance();
        setCreditBalance(balRes.data.balance);
        return;
      }
      if (res.data.status === 'expired') { toast.error('Payment expired.'); return; }
      setTimeout(() => pollPaymentStatus(stripeSessionId, attempts + 1), 2000);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user && !user.agreement_accepted) setShowAgreement(true);
    // Redirect new clients to onboarding wizard
    const onboardingDone = localStorage.getItem(`onboarding_complete_${user?.id}`);
    if (user && user.role === 'client' && !onboardingDone && !searchParams.get('session_id')) {
      navigate('/client/onboarding');
      return;
    }
    loadData();
    // Check for Stripe return
    const stripeSessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (stripeSessionId && payment === 'success') {
      toast.info('Checking payment status...');
      pollPaymentStatus(stripeSessionId);
    }
    if (payment === 'cancelled') toast.info('Payment cancelled.');
  }, [user, searchParams, pollPaymentStatus]);

  const loadData = async () => {
    try {
      const [sessRes, langRes, setRes, balRes, bunRes, bookRes] = await Promise.all([
        sessionsAPI.list(), languagesAPI.getAll(), creditsAPI.getSettings(),
        creditsAPI.getBalance(), creditsAPI.listBundles(), bookingsAPI.list()
      ]);
      setSessions(sessRes.data);
      setLanguages(langRes.data);
      setCreditSettings(setRes.data);
      setCreditBalance(balRes.data.balance || 0);
      setBundles(bunRes.data || []);
      setBookings(bookRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSeed = async () => {
    setSeeding(true);
    try { await seedAPI.seed(); window.location.reload(); } catch (e) { console.error(e); }
    setSeeding(false);
  };

  const handleAcceptAgreement = async () => { await acceptAgreement(); setShowAgreement(false); };

  const handleCreateSession = async () => {
    setCreating(true);
    try {
      const res = await sessionsAPI.create({
        source_language: 'en', target_language: selectedLang,
        domain_level: domainLevel, session_type: sessionType,
      });
      setShowNewSession(false);
      navigate(`/client/session/${res.data.id}`);
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const handleBuyCredits = async (bundleId) => {
    setPurchasing(bundleId);
    try {
      const originUrl = window.location.origin;
      const res = await creditsAPI.createCheckout({ bundle_id: bundleId, origin_url: originUrl });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error('Failed to start checkout');
    }
    setPurchasing(null);
  };

  const handleCreateBooking = async () => {
    if (!bookingForm.date || !bookingForm.time) { toast.error('Please select a date and time'); return; }
    const [hours, mins] = bookingForm.time.split(':').map(Number);
    const scheduledDate = new Date(bookingForm.date);
    scheduledDate.setHours(hours, mins, 0, 0);
    setBookingLoading(true);
    try {
      await bookingsAPI.create({
        target_language: bookingForm.target_language,
        domain_level: bookingForm.domain_level,
        scheduled_at: scheduledDate.toISOString(),
        duration_minutes: bookingForm.duration_minutes,
        notes: bookingForm.notes,
      });
      toast.success('Interpreter session scheduled!');
      setShowBooking(false);
      setBookingForm({ target_language: 'es', domain_level: 'BALANCED', date: null, time: '09:00', duration_minutes: 30, notes: '' });
      const bookRes = await bookingsAPI.list();
      setBookings(bookRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create booking');
    }
    setBookingLoading(false);
  };

  const handleCancelBooking = async (id) => {
    const booking = bookings.find(b => b.id === id);
    if (booking) {
      const scheduled = new Date(booking.scheduled_at);
      const hoursUntil = (scheduled - new Date()) / (1000 * 60 * 60);
      if (hoursUntil <= 24) {
        if (!window.confirm(`Cancelling within 24 hours incurs a fee of ${booking.duration_minutes} credits. Continue?`)) return;
      }
    }
    try {
      const res = await bookingsAPI.cancel(id);
      if (res.data.cancellation_fee > 0) {
        toast.warning(`Assignment cancelled. Cancellation fee: ${res.data.cancellation_fee} credits`);
      } else {
        toast.success('Assignment cancelled');
      }
      const bookRes = await bookingsAPI.list();
      setBookings(bookRes.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'active': return <div className="w-2 h-2 rounded-full bg-[hsl(var(--success))]" />;
      case 'ended': return <CheckCircle className="w-4 h-4 text-muted-foreground" />;
      case 'escalating': return <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))]" />;
      default: return <XCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const allLangs = [...(languages.sla || []), ...(languages.supported || [])];

  return (
    <AppShell>
      <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-in" data-testid="client-dashboard">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">Manage your interpretation sessions</p>
          </div>
          <div className="flex gap-3 items-center">
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-card">
              <Coins className="w-4 h-4 text-[hsl(var(--brand))]" />
              <span className="font-mono font-semibold text-sm" data-testid="credit-balance">{creditBalance}</span>
              <span className="text-xs text-muted-foreground">credits</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowBuyCredits(true)} data-testid="buy-credits-button">
              <CreditCard className="w-4 h-4 mr-2" />Buy Credits
            </Button>
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-data-button">
              <Database className="w-4 h-4 mr-2" />{seeding ? 'Seeding...' : 'Seed Demo'}
            </Button>
            <Button onClick={() => setShowNewSession(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="new-session-button">
              <Plus className="w-4 h-4 mr-2" />New Session
            </Button>
            <Button variant="outline" onClick={() => setShowBooking(true)} data-testid="schedule-interpreter-button">
              <CalendarIcon className="w-4 h-4 mr-2" />Schedule Interpreter
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {[
            { label: 'Active Sessions', value: sessions.filter(s => s.status === 'active').length, icon: Globe },
            { label: 'Total Sessions', value: sessions.length, icon: Clock },
            { label: 'Avg AI Rating', value: sessions.filter(s => s.ai_rating).length > 0 ? (sessions.reduce((a, s) => a + (s.ai_rating || 0), 0) / Math.max(sessions.filter(s => s.ai_rating).length, 1)).toFixed(1) : '-', icon: CheckCircle },
          ].map((stat, i) => (
            <div key={i} className={`rounded-lg border bg-card p-6 transition-all hover:shadow-md stagger-${i + 1} animate-fade-in`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-3xl font-bold mt-2 tracking-tight" style={{ fontFamily: 'DM Sans' }}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Upcoming Bookings */}
        {bookings.filter(b => b.status !== 'cancelled' && b.status !== 'completed').length > 0 && (
          <div className="rounded-lg border bg-card mb-6" data-testid="upcoming-bookings">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'DM Sans' }}>Upcoming Bookings</h2>
              <Badge variant="outline" className="text-xs">{bookings.filter(b => b.status !== 'cancelled' && b.status !== 'completed').length}</Badge>
            </div>
            <div className="divide-y">
              {bookings.filter(b => b.status !== 'cancelled' && b.status !== 'completed').map(b => (
                <div key={b.id} className="flex items-center justify-between p-4" data-testid={`booking-row-${b.id}`}>
                  <div className="flex items-center gap-4">
                    <CalendarIcon className="w-5 h-5 text-[hsl(var(--brand))]" />
                    <div>
                      <p className="font-medium text-sm">
                        English &rarr; {allLangs.find(l => l.code === b.target_language)?.name || b.target_language}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">{new Date(b.scheduled_at).toLocaleString()}</p>
                      {b.notes && <p className="text-xs text-muted-foreground mt-0.5">{b.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{b.duration_minutes} min</Badge>
                    <Badge variant={b.status === 'confirmed' ? 'default' : 'secondary'} className={`text-xs capitalize ${b.status === 'confirmed' ? 'bg-green-500/10 text-green-600 border-green-500/20' : b.status === 'awaiting_confirmation' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' : ''}`}>
                      {b.status === 'awaiting_confirmation' ? 'Awaiting Interpreter' : b.status}
                    </Badge>
                    {b.interpreter_name && <span className="text-xs text-muted-foreground">{b.interpreter_name}</span>}
                    {(b.status === 'pending' || b.status === 'confirmed') && (
                      <Button variant="ghost" size="sm" className="text-destructive text-xs h-7" onClick={() => handleCancelBooking(b.id)} data-testid={`cancel-booking-${b.id}`}>Cancel</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sessions List */}
        <div className="rounded-lg border bg-card">
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'DM Sans' }}>Recent Sessions</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No sessions yet. Start your first interpretation session.</p>
            </div>
          ) : (
            <div className="divide-y">
              {sessions.map(session => (
                <div key={session.id}
                  className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => ['active', 'escalating', 'human_connected'].includes(session.status) ? navigate(`/client/session/${session.id}`) : null}
                  data-testid={`session-row-${session.id}`}
                >
                  <div className="flex items-center gap-4">
                    {statusIcon(session.status)}
                    <div>
                      <p className="font-medium text-sm">
                        English &rarr; {allLangs.find(l => l.code === session.target_language)?.name || session.target_language}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">{new Date(session.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs gap-1">
                      {session.session_type === 'human' ? <Headphones className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                      {session.session_type === 'human' ? 'Human' : 'AI'}
                    </Badge>
                    <Badge variant={session.status === 'active' ? 'default' : 'secondary'} className="text-xs">{session.status}</Badge>
                    <Badge variant="outline" className="text-xs font-mono">{session.domain_level}</Badge>
                    {session.escalated && <Badge variant="destructive" className="text-xs">Escalated</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New Session Dialog */}
        <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
          <DialogContent className="max-w-md" data-testid="new-session-dialog">
            <DialogHeader>
              <DialogTitle>Start New Session</DialogTitle>
              <DialogDescription>Choose your interpreter type, language, and domain level.</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-4">
              {/* Interpreter Type Selection */}
              <div className="space-y-2">
                <Label>Interpreter Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSessionType('ai')}
                    className={`relative rounded-lg border-2 p-4 text-left transition-all ${
                      sessionType === 'ai' ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5' : 'border-border hover:border-muted-foreground/30'
                    }`}
                    data-testid="session-type-ai"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Bot className={`w-5 h-5 ${sessionType === 'ai' ? 'text-[hsl(var(--brand))]' : 'text-muted-foreground'}`} />
                      <span className="font-semibold text-sm">AI - Interpreter IO</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Coins className="w-3 h-3" />
                      <span className="font-mono">{creditSettings.ai_credits_per_minute} credit/min</span>
                    </div>
                    {sessionType === 'ai' && <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[hsl(var(--brand))]" />}
                  </button>
                  <button
                    onClick={() => setSessionType('human')}
                    className={`relative rounded-lg border-2 p-4 text-left transition-all ${
                      sessionType === 'human' ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5' : 'border-border hover:border-muted-foreground/30'
                    }`}
                    data-testid="session-type-human"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Headphones className={`w-5 h-5 ${sessionType === 'human' ? 'text-[hsl(var(--brand))]' : 'text-muted-foreground'}`} />
                      <span className="font-semibold text-sm">Human Interpreter</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Coins className="w-3 h-3" />
                      <span className="font-mono">{creditSettings.human_credits_per_minute} credits/min</span>
                    </div>
                    {sessionType === 'human' && <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[hsl(var(--brand))]" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Source Language</Label>
                <div className="p-3 rounded-md bg-secondary text-sm">English (US)</div>
              </div>
              <div className="space-y-2">
                <Label>Target Language</Label>
                <Select value={selectedLang} onValueChange={setSelectedLang}>
                  <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">SLA Languages (Priority)</div>
                    {(languages.sla || []).map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-1">Supported Languages</div>
                    {(languages.supported || []).map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Domain Level</Label>
                <Select value={domainLevel} onValueChange={setDomainLevel}>
                  <SelectTrigger data-testid="domain-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LITERAL">Literal - Word-for-word accuracy</SelectItem>
                    <SelectItem value="BALANCED">Balanced - Natural fluency (default)</SelectItem>
                    <SelectItem value="LEGAL_EQUIV">Legal Equivalence - Adapted concepts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewSession(false)}>Cancel</Button>
              <Button onClick={handleCreateSession} disabled={creating} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-session-button">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Start Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Buy Credits Dialog */}
        <Dialog open={showBuyCredits} onOpenChange={setShowBuyCredits}>
          <DialogContent className="max-w-md" data-testid="buy-credits-dialog">
            <DialogHeader>
              <DialogTitle>Buy Credits</DialogTitle>
              <DialogDescription>Select a credit bundle. You'll be redirected to Stripe for secure payment.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-lg border p-3 text-center bg-secondary">
                <p className="text-sm text-muted-foreground">Current Balance</p>
                <p className="text-3xl font-bold font-mono">{creditBalance}</p>
              </div>
              {bundles.map(b => {
                const discountedPrice = b.discount_percent ? Math.round(b.price_cents * (1 - b.discount_percent / 100)) : b.price_cents;
                return (
                  <div key={b.id} className="flex items-center justify-between rounded-lg border p-4 hover:bg-accent/50 transition-colors" data-testid={`bundle-${b.id}`}>
                    <div>
                      <p className="font-semibold text-sm">{b.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{b.credits} credits</p>
                      {b.discount_percent > 0 && <Badge variant="secondary" className="text-[10px] mt-1">{b.discount_percent}% off</Badge>}
                    </div>
                    <Button size="sm" onClick={() => handleBuyCredits(b.id)} disabled={purchasing === b.id}
                      className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white"
                      data-testid={`buy-bundle-${b.id}`}>
                      {purchasing === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `$${(discountedPrice / 100).toFixed(2)}`}
                    </Button>
                  </div>
                );
              })}
              {bundles.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No bundles available</p>}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowBuyCredits(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Agreement Dialog */}
        <Dialog open={showAgreement} onOpenChange={() => {}}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Service Agreement</DialogTitle>
              <DialogDescription>Please review and accept our terms before proceeding.</DialogDescription>
            </DialogHeader>
            <div className="max-h-60 overflow-y-auto text-sm text-muted-foreground space-y-3 p-4 bg-secondary rounded-lg">
              <p>By using AI Interpreter IO, you agree to the following terms:</p>
              <p>1. AI interpretation is provided as an assistive tool and should not replace professional human interpreters in critical legal proceedings.</p>
              <p>2. All sessions may be recorded for quality assurance purposes.</p>
              <p>3. You acknowledge that AI translation may contain errors and agree to use the escalation feature when accuracy is critical.</p>
              <p>4. This service is available for US-based users only.</p>
              <p>5. By accepting, you confirm that you understand the advisory system and will heed HIGH_RISK warnings.</p>
            </div>
            <DialogFooter>
              <Button onClick={handleAcceptAgreement} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="accept-agreement-button">
                I Accept
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule Interpreter Booking Dialog */}
        <Dialog open={showBooking} onOpenChange={setShowBooking}>
          <DialogContent className="max-w-md" data-testid="schedule-booking-dialog">
            <DialogHeader>
              <DialogTitle>Schedule Human Interpreter</DialogTitle>
              <DialogDescription>Pre-book a human interpreter for a future session. Must be at least 30 minutes ahead.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={bookingForm.target_language} onValueChange={v => setBookingForm(p => ({ ...p, target_language: v }))}>
                  <SelectTrigger data-testid="booking-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allLangs.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full justify-start text-left font-normal ${!bookingForm.date ? 'text-muted-foreground' : ''}`} data-testid="booking-date-picker">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {bookingForm.date ? new Date(bookingForm.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={bookingForm.date}
                      onSelect={(d) => setBookingForm(p => ({ ...p, date: d }))}
                      disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input type="time" value={bookingForm.time}
                  onChange={e => setBookingForm(p => ({ ...p, time: e.target.value }))}
                  className="font-mono"
                  data-testid="booking-time-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Select value={String(bookingForm.duration_minutes)} onValueChange={v => setBookingForm(p => ({ ...p, duration_minutes: parseInt(v) }))}>
                    <SelectTrigger data-testid="booking-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Domain Level</Label>
                  <Select value={bookingForm.domain_level} onValueChange={v => setBookingForm(p => ({ ...p, domain_level: v }))}>
                    <SelectTrigger data-testid="booking-domain-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LITERAL">Literal</SelectItem>
                      <SelectItem value="BALANCED">Balanced</SelectItem>
                      <SelectItem value="LEGAL_EQUIV">Legal Equivalence</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea value={bookingForm.notes} onChange={e => setBookingForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Context for the interpreter (e.g., topic, special terminology)..."
                  rows={3} data-testid="booking-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBooking(false)}>Cancel</Button>
              <Button onClick={handleCreateBooking} disabled={bookingLoading || !bookingForm.date || !bookingForm.time}
                className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-booking-button">
                {bookingLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarIcon className="w-4 h-4 mr-2" />}
                Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
